import { Injectable, inject } from '@angular/core'

import type { Observable } from 'rxjs'
import { catchError, map, of, switchMap } from 'rxjs'

import { ApiService } from '../../api/api.service'
import type {
  CreateAccountResponseDto,
  UpdateUserResponseDto,
  UsersUserDto,
  VerifyUserResponseDto,
} from '../../api/responses'
import { UserRole } from '../../types/types'
import { AuthService } from '../auth/auth.service'

@Injectable({
  providedIn: 'root',
})
export class UsersService {
  private readonly apiService = inject(ApiService)
  private readonly authService = inject(AuthService)

  createUser(
    email: string,
    password: string,
    name: string,
    firstName: string,
    lastName: string,
    recaptchaToken?: string,
    articleToPurchase?: number
  ): Observable<CreateAccountResponseDto | null | undefined> {
    return this.apiService.createUser(
      email,
      password,
      name,
      firstName,
      lastName,
      recaptchaToken,
      articleToPurchase
    )
  }

  updateUserRoles(
    userId: number,
    roles: UserRole[]
  ): Observable<UpdateUserResponseDto | null | undefined> {
    return this.apiService
      .updateUser(userId, { roles: roles })
      .pipe(map(result => result.data))
  }

  verifyUser(data: {
    token: string
    purchaseId?: number | null
  }): Observable<VerifyUserResponseDto | null | undefined> {
    return this.apiService.verifyUser(data)
  }

  private getOrganizationUsers(refresh: boolean): Observable<UsersUserDto[]> {
    return this.authService.currentUser$.pipe(
      switchMap(user => {
        const organizationId = user?.organization?.id
        if (!organizationId) {
          throw Error('pages.organization.errors.noOrganization')
        }
        return this.apiService.getUsersByOrganization(
          organizationId,
          0,
          -1,
          refresh ? 'network-only' : 'cache-first'
        )
      }),
      map(response => response.data.Users.docs),
      catchError(() => of([]))
    )
  }

  getUsers(refresh = false): Observable<UsersUserDto[]> {
    return this.getOrganizationUsers(refresh)
  }

  getOrganizationUserCount(): Observable<number> {
    return this.authService.currentUser$.pipe(
      switchMap(user => {
        const organizationId = user?.organization?.id
        if (!organizationId) {
          throw Error('pages.organization.errors.noOrganization')
        }
        return this.apiService.getUserCountByOrganization(organizationId)
      }),
      map(response => response.data.Users.totalDocs),
      catchError(() => of(0))
    )
  }

  removeUserFromOrganization(
    userId: number
  ): Observable<UpdateUserResponseDto | null> {
    return this.apiService
      .updateUser(userId, { organization: null, roles: [UserRole.Customer] })
      .pipe(
        map(result => result.data ?? null),
        switchMap(response => {
          if (!response) {
            return of(null)
          }

          return this.authService.refreshUserDetails().pipe(
            map(() => response),
            catchError(() => of(response))
          )
        })
      )
  }
}
